import './AddChat.scss';
import 'react-toastify/dist/ReactToastify.css';

import {useCallback, useState, useContext, useEffect} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { v4 } from 'uuid';
import * as Yup from 'yup';

import chevronLeft from '../../../assets/images/icons/chevron-left.svg';
import Image from '../../../components/Image';
import InputForm from '../../../components/InputForm/InputForm';
import SideBar from '../../../components/SideBar/SideBar';
import TopNav from '../../../components/TopNav/TopNav';
import { toast } from 'react-toastify';
import Button from '../../../components/Buttons/Button';
import CustomDatePicker from "../../../components/CustomDatePicker";
import { FormDataContext } from '../../../FormDataContext';

const AddChat = () => {
    const navigate = useNavigate();
    const { formData, setFormData } = useContext(FormDataContext);

    const [chatName, setChatName] = useState("");
    const [expiryDate, setExpiryDate] = useState(null);
    const [venueImages, setVenueImages] = useState([{ id: v4() }]);
    const [errors, setErrors] = useState({});

    const handleImageAdd = useCallback((event) => {
        const file = event.target.files[0];
        const image = { id: v4(), file };
        setVenueImages((prev) => [...prev, image]);
    }, []);

    const handleImageRemove = useCallback((id) => {
        setVenueImages((prev) => prev.filter((img) => img.id !== id));
    }, []);

    useEffect(() => {
        if (formData) {
            setChatName(formData.name || "");
            setExpiryDate(formData.expiryDate || null);
        }
    }, [formData]);

    const handleDateChange = (date) => {
        setExpiryDate(date);
    };

    const validationSchema = Yup.object().shape({
        chatName: Yup.string().required("Name is required"),
        expiryDate: Yup.date().required("Expiry date is required"),
    });

    const handleProceed = async (event) => {
        event.preventDefault();

        try {
            await validationSchema.validate(
                { chatName, expiryDate },
                { abortEarly: false }
            );

            // Create a new FormData object and append chat details
            const newFormData = new FormData();
            newFormData.append("name", chatName);
            newFormData.append("expiryDate", expiryDate);

            // Append all image files to the FormData
            venueImages.forEach((img) => {
                if (img && img.file) {  // Ensure img and img.file are valid
                    newFormData.append("images", img.file);
                } else {
                    console.warn("Invalid image:", img);
                }
            });


            const formDataMeta = {
                name: chatName,
                expiryDate: expiryDate,
                imageFiles: venueImages.map((img) => img.file),
            };

            setFormData(formDataMeta);

            // Navigate to the AddParticipants component.
            navigate("/add-participants");
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                const validationErrors = error.inner.reduce((acc, err) => {
                    acc[err.path] = err.message;
                    return acc;
                }, {});
                setErrors(validationErrors);
                toast.error("Validation Error");
            } else {
                console.error("Error preparing form data: ", error);
                toast.error("An error occurred. Please try again.");
            }
        }
    };

    return (
        <main>
            <TopNav />
            <div className="main-container">
                <SideBar />
                <article className="page-content">
                    <section className="add-venue">
                        <div className="add-venue__top">
                            <Link to="/chats">
                                <img src={chevronLeft} className="add-venue__top-icon" alt="Back" />
                            </Link>
                            <h1 className="add-venue__top-heading">Create Chat</h1>
                        </div>

                        <form onSubmit={handleProceed} className="add-venue__form">
                            <section className="add-venue__form--left">
                                <div className="add-venue__form-block">
                                    <div className="add-venue__form-block-group">
                                        <InputForm
                                            placeholder="Name"
                                            label="Chat Name"
                                            type="text"
                                            id="chatName"
                                            name="chatName"
                                            value={chatName}
                                            onChange={(e) => setChatName(e.target.value)}
                                            error={errors.chatName}
                                        />
                                    </div>

                                    <div className="add-venue__form-block-group">
                                        <CustomDatePicker
                                            label="Chat Expiry Date"
                                            selectedDate={expiryDate}
                                            onChange={handleDateChange}
                                            error={errors.expiryDate}
                                        />
                                    </div>
                                </div>

                                {/* Group Chat Display Image - Only One Image Upload */}
                                <label style={{ display: 'block', marginBottom: 8, fontWeight: '500' }}>
                                    Display Image
                                </label>
                                <div className="add-venue__form-block" style={{ marginBottom: 20 }}>
                                    <Image
                                        onAdd={handleImageAdd}
                                        onRemove={handleImageRemove}
                                    />
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button label="Add Participants" type="submit" primaryBtn />
                                </div>
                            </section>
                        </form>
                    </section>
                </article>
            </div>
        </main>
    );
};

export default AddChat;
